import { forwardRef, useState, useEffect, useCallback, useRef, useImperativeHandle } from 'react';
import { HiX } from 'react-icons/hi';

import { Modal, ModalMethods, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';

import QRImage from '../../../../assets/images/QR.png';
import BarcodeImage from '../../../../assets/images/barcode.png';

type LabelType = 'QR_LABEL' | 'BARCODE_LABEL';
type PaperSize = 'A4_SHEET' | 'LABEL_PRINTER';
type QuantityMode = 'ONE_PER_ITEM' | 'AS_PER_CURRENT_ITEM_QUANTITY' | 'CUSTOM';

type LabelConfig = {
  label_type: LabelType;
  paper_size: PaperSize;
  label_printer_size: string;
  sheet_columns: number;
  sheet_rows: number;
  start_position: number;
  quantity_mode: QuantityMode;
  custom_quantity: number;
};

type LabelConfigModalProps = {
  readymadeId: number;
  variantId: number;
  itemName: string;
  price: number;
  onSaved: () => void;
};

const DEFAULT_CONFIG: LabelConfig = {
  label_type: 'QR_LABEL',
  paper_size: 'A4_SHEET',
  label_printer_size: '2.5 x 1 in',
  sheet_columns: 3,
  sheet_rows: 8,
  start_position: 1,
  quantity_mode: 'ONE_PER_ITEM',
  custom_quantity: 100,
};

const LABEL_SIZES = [
  { label: 'Small (2.5in X 1in)', value: '2.5 x 1 in' },
  { label: 'Medium (2.5in X 1.3in)', value: '2.5 x 1.3 in' },
  { label: 'Large (4in X 2in)', value: '4 x 2 in' },
];

const QUANTITY_OPTIONS: { label: string; value: QuantityMode }[] = [
  { label: '1 per item', value: 'ONE_PER_ITEM' },
  { label: 'As per current item quantity', value: 'AS_PER_CURRENT_ITEM_QUANTITY' },
  { label: 'Custom', value: 'CUSTOM' },
];

const parseLabelDimensions = (sizeStr: string): { width: string; height: string } => {
  const match = sizeStr.match(/^([\d.]+)\s*x\s*([\d.]+)\s*(.+)$/i);

  if (match) {
    return { width: `${match[1]} ${match[3]}`, height: `${match[2]} ${match[3]}` };
  }

  return { width: '2.5 In', height: '1.3 in' };
};

const LabelConfigModal = forwardRef<ModalMethods, LabelConfigModalProps>(
  ({ readymadeId, variantId, itemName, price, onSaved }, ref) => {
    const [config, setConfig] = useState<LabelConfig>(DEFAULT_CONFIG);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState<1 | 2>(1);
    const [showQtyDropdown, setShowQtyDropdown] = useState(false);
    const [showSizeDropdown, setShowSizeDropdown] = useState(false);
    const [showStartGrid, setShowStartGrid] = useState(true);

    const internalModalRef = useRef<ModalMethods>(null);
    const qtyDropdownRef = useRef<HTMLDivElement>(null);
    const sizeDropdownRef = useRef<HTMLDivElement>(null);

    const fetchConfig = useCallback(async () => {
      if (!readymadeId || !variantId) {
        setConfig(DEFAULT_CONFIG);

        return;
      }

      try {
        setIsLoading(true);
        const response = await api.getRequest(
          `readymade/${readymadeId}/label-config?variant_id=${variantId}`
        );

        if (response.status && response.data) {
          const d = response.data;

          setConfig({
            label_type: d.label_type || DEFAULT_CONFIG.label_type,
            paper_size: d.paper_size || DEFAULT_CONFIG.paper_size,
            label_printer_size: d.label_printer_size || DEFAULT_CONFIG.label_printer_size,
            sheet_columns: d.sheet_columns ?? DEFAULT_CONFIG.sheet_columns,
            sheet_rows: d.sheet_rows ?? DEFAULT_CONFIG.sheet_rows,
            start_position: d.start_position ?? DEFAULT_CONFIG.start_position,
            quantity_mode: d.quantity_mode || DEFAULT_CONFIG.quantity_mode,
            custom_quantity: d.custom_quantity ?? DEFAULT_CONFIG.custom_quantity,
          });
        } else {
          setConfig(DEFAULT_CONFIG);
        }
      } catch {
        setConfig(DEFAULT_CONFIG);
      } finally {
        setIsLoading(false);
      }
    }, [readymadeId, variantId]);

    useImperativeHandle(
      ref,
      () => ({
        show: () => {
          internalModalRef.current?.show();
          setStep(1);
          setShowQtyDropdown(false);
          setShowSizeDropdown(false);
          fetchConfig();
        },
        hide: () => {
          internalModalRef.current?.hide();
        },
        toggle: () => {
          internalModalRef.current?.toggle();
        },
        getModalState: () => internalModalRef.current?.getModalState() ?? false,
      }),
      [fetchConfig]
    );

    useEffect(() => {
      const handleClick = (e: MouseEvent) => {
        if (qtyDropdownRef.current && !qtyDropdownRef.current.contains(e.target as Node)) {
          setShowQtyDropdown(false);
        }

        if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(e.target as Node)) {
          setShowSizeDropdown(false);
        }
      };

      document.addEventListener('mousedown', handleClick);

      return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const updateConfig = useCallback((key: keyof LabelConfig, value: string | number) => {
      setConfig((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handlePaperSizeChange = useCallback((size: PaperSize) => {
      setConfig((prev) => ({
        ...prev,
        paper_size: size,
        ...(size === 'A4_SHEET'
          ? { sheet_columns: prev.sheet_columns || 3, sheet_rows: prev.sheet_rows || 8 }
          : { start_position: 1 }),
      }));
    }, []);

    const handleSaveConfig = async () => {
      try {
        setIsSaving(true);
        const payload: Record<string, unknown> = {
          label_type: config.label_type,
          paper_size: config.paper_size,
          sheet_columns: config.sheet_columns,
          sheet_rows: config.sheet_rows,
          start_position: config.start_position,
          quantity_mode: config.quantity_mode,
        };

        if (config.paper_size === 'LABEL_PRINTER') {
          payload.label_printer_size = config.label_printer_size;
        }

        if (config.quantity_mode === 'CUSTOM') {
          payload.custom_quantity = config.custom_quantity;
        }

        const response = await api.putRequest(
          `readymade/${readymadeId}/label-config?variant_id=${variantId}`,
          payload
        );

        if (response.status) {
          toasts('success', 'Label configuration saved', 'label-config-success');
          setStep(2);
        } else {
          toasts('error', response.message || 'Failed to save label config', 'label-config-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'label-config-error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const handlePrint = async () => {
      try {
        setIsSaving(true);
        const response = await api.postRequest(
          `readymade/${readymadeId}/label-print?format=pdf&variant_id=${variantId}`,
          { apply_to_all: false, variant_ids: [variantId] }
        );

        if (response.status) {
          if (response.data?.pdfUrl) {
            window.open(response.data.pdfUrl, '_blank');
          }

          toasts('success', 'Labels generated successfully', 'label-print-success');
          internalModalRef.current?.hide();
          onSaved();
        } else {
          toasts('error', response.message || 'Failed to generate labels', 'label-print-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'label-print-error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const handleClose = () => {
      internalModalRef.current?.hide();
      setStep(1);
    };

    const handleBack = () => {
      if (step === 2) {
        setStep(1);
      } else {
        handleClose();
      }
    };

    const totalCells = config.sheet_columns * config.sheet_rows;
    const isQR = config.label_type === 'QR_LABEL';
    const dimensions = parseLabelDimensions(config.label_printer_size);

    const getQuantityDisplay = () => {
      if (config.quantity_mode === 'ONE_PER_ITEM') {
        return '1';
      }

      if (config.quantity_mode === 'CUSTOM') {
        return 'Custom';
      }

      return 'Qty';
    };

    const getSizeLabel = () => {
      const size = LABEL_SIZES.find((s) => s.value === config.label_printer_size);

      return size?.label || config.label_printer_size || 'Size';
    };

    const renderToggleButton = (
      label: string,
      isActive: boolean,
      onClick: () => void,
      icon?: JSX.Element
    ) => (
      <button
        type="button"
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-inter font-semibold transition-colors ${
          isActive
            ? 'bg-gray-900 text-white'
            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
        }`}
      >
        {icon}
        {label}
      </button>
    );

    const QRIcon = (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect
          x="1"
          y="1"
          width="5"
          height="5"
          rx="0.5"
          stroke="currentColor"
          strokeWidth="1.2"
          fill="none"
        />
        <rect
          x="10"
          y="1"
          width="5"
          height="5"
          rx="0.5"
          stroke="currentColor"
          strokeWidth="1.2"
          fill="none"
        />
        <rect
          x="1"
          y="10"
          width="5"
          height="5"
          rx="0.5"
          stroke="currentColor"
          strokeWidth="1.2"
          fill="none"
        />
        <rect x="3" y="3" width="1.5" height="1.5" fill="currentColor" />
        <rect x="12" y="3" width="1.5" height="1.5" fill="currentColor" />
        <rect x="3" y="12" width="1.5" height="1.5" fill="currentColor" />
        <rect x="10" y="10" width="2" height="2" fill="currentColor" />
        <rect x="13" y="13" width="2" height="2" fill="currentColor" />
      </svg>
    );

    const BarcodeIcon = (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="1" y="3" width="1.2" height="10" fill="currentColor" />
        <rect x="3.5" y="3" width="0.8" height="10" fill="currentColor" />
        <rect x="5.5" y="3" width="1.5" height="10" fill="currentColor" />
        <rect x="8" y="3" width="0.8" height="10" fill="currentColor" />
        <rect x="10" y="3" width="1.2" height="10" fill="currentColor" />
        <rect x="12.5" y="3" width="0.8" height="10" fill="currentColor" />
        <rect x="14" y="3" width="1" height="10" fill="currentColor" />
      </svg>
    );

    const ChevronDown = (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path
          d="M3 4.5l3 3 3-3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

    const Checkmark = (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M3 8l3.5 3.5L13 5"
          stroke="#111"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

    const renderStartPositionGrid = () => {
      if (config.paper_size === 'LABEL_PRINTER') {
        return (
          <p className="text-xs text-gray-400 font-inter py-4 text-center">
            Start position is not applicable for Label Printer
          </p>
        );
      }

      return (
        <div
          className="grid gap-0.5 mt-3"
          style={{ gridTemplateColumns: `repeat(${config.sheet_columns}, 40px)` }}
        >
          {Array.from({ length: totalCells }, (_, i) => {
            const pos = i + 1;
            const isSkipped = pos < config.start_position;
            const labelNum = pos - config.start_position + 1;

            return (
              <div
                key={pos}
                onClick={() => updateConfig('start_position', pos)}
                className={`h-7 flex items-center justify-center border text-xs cursor-pointer select-none transition-colors ${
                  isSkipped
                    ? 'bg-gray-300 border-gray-400'
                    : 'bg-white border-gray-300 text-gray-700 font-medium hover:bg-blue-50'
                }`}
              >
                {isSkipped ? '' : labelNum}
              </div>
            );
          })}
        </div>
      );
    };

    const renderSingleLabelPreview = () => {
      const labelImage = isQR ? QRImage : BarcodeImage;

      return (
        <div className="relative mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="flex-1 h-px bg-gray-300" />
            <span className="text-[11px] text-gray-500 font-inter whitespace-nowrap px-1">
              ← {dimensions.width} →
            </span>
            <div className="flex-1 h-px bg-gray-300" />
          </div>

          <div className="flex">
            <div className="flex-1 border border-gray-300 rounded-lg p-3 bg-white">
              {isQR ? (
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-inter font-semibold text-gray-800 leading-tight">
                      {itemName || 'Item Name Displayed here'}
                    </p>
                    <p className="text-[11px] font-inter font-bold text-gray-900 mt-2">Price</p>
                    <p className="text-[11px] font-inter text-gray-600">
                      Rs {price ? price.toFixed(2) : 'XX.XX'}
                    </p>
                  </div>
                  <div className="flex flex-col items-center flex-shrink-0">
                    <img src={labelImage} alt="QR Code" className="w-14 h-14 object-contain" />
                    <p className="text-[7px] text-gray-400 mt-1 font-inter tracking-wide">
                      XXX•XX•XXX•XXX
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center text-center">
                  <p className="text-[11px] font-inter font-semibold text-gray-800 mb-2">
                    {itemName || 'Item Name Displayed here'}
                  </p>
                  <img
                    src={labelImage}
                    alt="Barcode"
                    className="w-full max-w-[200px] h-12 object-contain"
                  />
                  <p className="text-[7px] text-gray-400 mt-1 font-inter tracking-wide">
                    XXX•XX•XXX•X00
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col items-center justify-center ml-3 w-10">
              <div className="flex-1 w-px bg-gray-300" />
              <span className="text-[10px] text-gray-500 font-inter whitespace-nowrap my-1">
                {dimensions.height}
              </span>
              <div className="flex-1 w-px bg-gray-300" />
            </div>
          </div>
        </div>
      );
    };

    const renderSheetPreview = () => {
      if (config.paper_size === 'LABEL_PRINTER') {
        return (
          <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 flex items-center justify-center">
            <p className="text-xs text-gray-400 font-inter">
              Sheet preview not available for Label Printer
            </p>
          </div>
        );
      }

      const cols = config.sheet_columns;
      const rows = config.sheet_rows;
      const labelImage = isQR ? QRImage : BarcodeImage;

      return (
        <div className="relative">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="flex-1 h-px bg-gray-300" />
            <span className="text-[11px] text-gray-500 font-inter whitespace-nowrap px-1">
              {cols} columns
            </span>
            <div className="flex-1 h-px bg-gray-300" />
          </div>

          <div className="flex">
            <div className="flex-1 border border-gray-200 rounded-lg p-2 bg-gray-50 overflow-hidden">
              <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                {Array.from({ length: cols * rows }, (_, i) => {
                  const pos = i + 1;
                  const isSkipped = pos < config.start_position;

                  return (
                    <div
                      key={pos}
                      className={`border rounded p-1 ${
                        isSkipped ? 'bg-gray-200 border-gray-300' : 'bg-white border-gray-200'
                      }`}
                      style={{ minHeight: 30 }}
                    >
                      {!isSkipped && (
                        <div className="flex items-center gap-0.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-[5px] text-gray-600 font-inter truncate leading-tight">
                              {itemName || 'Item Name'}
                            </p>
                            <p className="text-[5px] text-gray-500 font-inter leading-tight">
                              Rs {price || 'XX'}
                            </p>
                          </div>
                          <img
                            src={labelImage}
                            alt=""
                            className="w-3.5 h-3.5 object-contain flex-shrink-0"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center ml-3 w-10">
              <div className="flex-1 w-px bg-gray-300" />
              <span className="text-[10px] text-gray-500 font-inter whitespace-nowrap my-1">
                {rows} rows
              </span>
              <div className="flex-1 w-px bg-gray-300" />
            </div>
          </div>
        </div>
      );
    };

    const renderStep2 = () => (
      <div className="flex flex-col items-center justify-center h-full py-8">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 13l4 4L19 7"
              stroke="#22c55e"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3 className="font-inter font-bold text-lg text-gray-900 mb-2">Configuration Saved!</h3>
        <p className="font-inter text-sm text-gray-500 text-center mb-6 max-w-sm">
          Your label configuration has been saved. Click Save to generate and download the labels.
        </p>
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 w-full max-w-sm">
          <div className="space-y-2 text-sm font-inter">
            <div className="flex justify-between">
              <span className="text-gray-500">Label Type</span>
              <span className="text-gray-800 font-medium">
                {isQR ? 'QR Label' : 'Barcode Label'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Paper Size</span>
              <span className="text-gray-800 font-medium">
                {config.paper_size === 'A4_SHEET' ? 'A4 Sheet' : 'Label Printer'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Quantity</span>
              <span className="text-gray-800 font-medium">
                {QUANTITY_OPTIONS.find((o) => o.value === config.quantity_mode)?.label}
                {config.quantity_mode === 'CUSTOM' ? ` (${config.custom_quantity})` : ''}
              </span>
            </div>
            {config.paper_size === 'A4_SHEET' && (
              <div className="flex justify-between">
                <span className="text-gray-500">Start Position</span>
                <span className="text-gray-800 font-medium">{config.start_position}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );

    const headerComponent = (
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleClose}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <HiX size={20} />
        </button>
        <span className="font-inter font-semibold text-base text-gray-900">Label Size</span>
      </div>
    );

    const footerComponent = (
      <div className="flex items-center justify-between w-full">
        <button
          type="button"
          onClick={handleBack}
          className="font-inter text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
        >
          Back
        </button>
        <span className="font-inter text-sm text-gray-500">Steps {step}/2</span>
        <button
          type="button"
          onClick={step === 1 ? handleSaveConfig : handlePrint}
          disabled={isSaving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    );

    return (
      <Modal
        ref={internalModalRef}
        size="xl"
        headerComponent={headerComponent}
        footerComponent={footerComponent}
        showCloseIcon={false}
        showFooter={true}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : step === 2 ? (
          renderStep2()
        ) : (
          <div className="flex gap-6 py-4 h-full">
            {/* Left Panel – Form */}
            <div className="flex-1 overflow-y-auto pr-2 min-w-0">
              {/* Label Type */}
              <div className="mb-5">
                <p className="text-xs text-gray-500 font-inter font-semibold uppercase tracking-wider mb-2">
                  Label Type
                </p>
                <div className="flex gap-2">
                  {renderToggleButton(
                    'QR Label',
                    config.label_type === 'QR_LABEL',
                    () => updateConfig('label_type', 'QR_LABEL'),
                    QRIcon
                  )}
                  {renderToggleButton(
                    'Barcode Label',
                    config.label_type === 'BARCODE_LABEL',
                    () => updateConfig('label_type', 'BARCODE_LABEL'),
                    BarcodeIcon
                  )}
                </div>
              </div>

              <hr className="border-gray-200 mb-5" />

              {/* Paper Size */}
              <div className="mb-5">
                <p className="text-xs text-gray-500 font-inter font-semibold uppercase tracking-wider mb-2">
                  Paper Size
                </p>
                <div className="flex gap-2">
                  {renderToggleButton('A4 SHEET', config.paper_size === 'A4_SHEET', () =>
                    handlePaperSizeChange('A4_SHEET')
                  )}
                  {renderToggleButton('Label Printer', config.paper_size === 'LABEL_PRINTER', () =>
                    handlePaperSizeChange('LABEL_PRINTER')
                  )}
                </div>
              </div>

              <hr className="border-gray-200 mb-5" />

              {/* Label Quantity */}
              <div className="mb-5">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-sm text-gray-700 font-inter font-medium">Label Quantity</p>
                  <div className="relative" ref={qtyDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowQtyDropdown(!showQtyDropdown)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white rounded-full text-sm font-inter font-medium"
                    >
                      {getQuantityDisplay()}
                      {ChevronDown}
                    </button>
                    {showQtyDropdown && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[220px]">
                        {QUANTITY_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              updateConfig('quantity_mode', opt.value);
                              setShowQtyDropdown(false);
                            }}
                            className="flex items-center justify-between w-full px-4 py-2.5 text-sm font-inter text-gray-700 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                          >
                            {opt.label}
                            {config.quantity_mode === opt.value && Checkmark}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {config.quantity_mode === 'CUSTOM' && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 font-inter">Amount</span>
                      <input
                        type="number"
                        value={config.custom_quantity || ''}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);

                          updateConfig('custom_quantity', isNaN(val) ? 0 : val);
                        }}
                        className="w-20 border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-inter text-gray-800 outline-none focus:border-gray-400"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Start Position */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-700 font-inter font-medium">
                    Choose label print start position
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowStartGrid((prev) => !prev)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      showStartGrid ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        showStartGrid ? 'left-[18px]' : 'left-0.5'
                      }`}
                    />
                  </button>
                </div>

                {showStartGrid && config.paper_size === 'A4_SHEET' && (
                  <div className="flex gap-2 mb-2">
                    {renderToggleButton('A4 SHEET', true, () => {})}
                    {renderToggleButton('Label Printer', false, () =>
                      handlePaperSizeChange('LABEL_PRINTER')
                    )}
                  </div>
                )}

                {showStartGrid && renderStartPositionGrid()}
              </div>

              <hr className="border-gray-200 mb-5" />

              {/* Label Size */}
              <div className="mb-2">
                <p className="text-xs text-gray-500 font-inter font-semibold uppercase tracking-wider mb-2">
                  Label Size
                </p>
                <div className="relative" ref={sizeDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowSizeDropdown(!showSizeDropdown)}
                    className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-inter text-gray-700 hover:border-gray-400 transition-colors bg-white"
                  >
                    {getSizeLabel()}
                    {ChevronDown}
                  </button>
                  {showSizeDropdown && (
                    <div className="absolute bottom-full left-0 mb-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                      {LABEL_SIZES.map((size) => (
                        <button
                          key={size.value}
                          type="button"
                          onClick={() => {
                            updateConfig('label_printer_size', size.value);
                            setShowSizeDropdown(false);
                          }}
                          className="flex items-center justify-between w-full px-4 py-2.5 text-sm font-inter text-gray-700 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                        >
                          {size.label}
                          {config.label_printer_size === size.value && Checkmark}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel – Preview */}
            <div className="w-[340px] flex-shrink-0 overflow-y-auto pl-2">
              {renderSingleLabelPreview()}
              {renderSheetPreview()}
            </div>
          </div>
        )}
      </Modal>
    );
  }
);

LabelConfigModal.displayName = 'LabelConfigModal';

export default LabelConfigModal;
